import { Signal, useComputed } from "@suse-o11y/signal";
import React from "react";
import { useIntersection } from "react-use";

import { SkeletonBox } from "~/components/skeleton/box";
import { TableColFilter } from "~/components/table/col-filter";
import { TableColOrder } from "~/components/table/col-order";
import { StickyFrame } from "~/components/table/sticky-frame";
import type { Time } from "~/tools/date-time/time";
import type { Record } from "~/tools/record";
import { spacing } from "~/tools/spacing";
import { usePermanent } from "~/tools/use-permanent";

import {
	Api,
	type HealthStateValue,
	type MonitorPreviewResult,
	type MonitorRuntimeMetrics,
	type MonitorStatus,
} from "../../common/openapi";
import {
	queryMonitorCheckStates,
	queryMonitorStatus,
	queryTestMonitorCheckStates,
	queryTestMonitorFunction,
} from "../../queries/monitors";
import { useQuery } from "../../queries/react-query";
import { GenericErrorMessage } from "../react/error/GenericErrorMessage";
import { Tab, Tabs } from "../react/tabs/Tabs";
import { ScrollToTop } from "../scroll-to-top";

import styles from "./monitor-output-drawer.module.css";
import type { MonitorConfig, MonitorOutputDrawerState } from "./monitor-output-drawer-state";
import { MonitorOutputDrawerTable } from "./monitor-output-drawer-table";

const MONITOR_OUTPUT_DRAWER_TABS = ["All", "Critical", "Deviating", "Clear", "Unknown"] as const;

type MonitorOutputDrawerTab = (typeof MONITOR_OUTPUT_DRAWER_TABS)[number];

type MonitorOutputCounters = Record<MonitorOutputDrawerTab, number>;

const MONITOR_OUTPUT_DRAWER_TAB_TO_HEALTH_STATE = {
	All: undefined,
	Critical: Api.HealthStateValue.CRITICAL,
	Deviating: Api.HealthStateValue.DEVIATING,
	Clear: Api.HealthStateValue.CLEAR,
	Unknown: Api.HealthStateValue.UNKNOWN,
} satisfies Record<MonitorOutputDrawerTab>;

const monitorMetricsToCounters = ({
	healthStatesCount = 0,
	unmappedHealthStatesCount = 0,
	criticalCount = 0,
	deviatingCount = 0,
	clearCount = 0,
	unknownCount = 0,
}: MonitorRuntimeMetrics): MonitorOutputCounters => ({
	All: healthStatesCount - unmappedHealthStatesCount,
	Critical: criticalCount,
	Deviating: deviatingCount,
	Clear: clearCount,
	Unknown: unknownCount,
});

const monitorPreviewToCounters = (preview: MonitorPreviewResult): MonitorOutputCounters => ({
	All: preview.healthStatesCount,
	Critical: preview.criticalCount,
	Deviating: preview.deviatingCount,
	Clear: preview.clearCount,
	Unknown: preview.unknownCount,
});

export const MonitorOutputDrawerContent: React.FC<MonitorOutputDrawerState> = ({
	monitor,
	selectedTab,
	lastSuccessfulRunTimestamp,
}) => {
	const selectedTabKey = useComputed(selectedTab);

	const orders = usePermanent(() => ({
		healthState: Signal(TableColOrder.init()),
		componentName: Signal(TableColOrder.init()),
		componentType: Signal(TableColOrder.init()),
		lastUpdateTimestamp: Signal(TableColOrder.init()),
	}));

	const filters = usePermanent(() => ({
		componentName: Signal(TableColFilter.init()),
		componentType: Signal(TableColFilter.init()),
	}));

	const query =
		monitor.type === "MONITOR"
			? queryMonitorStatus(
					{
						monitorIdOrUrn: monitor.monitorId,
						timestamp: lastSuccessfulRunTimestamp,
					},
					{
						select: ({ metrics }) => monitorMetricsToCounters(metrics.runtimeMetrics),
					},
				)
			: queryTestMonitorFunction(
					{
						monitorFunctionIdOrUrn: monitor.monitorFunctionId,
						arguments: monitor.arguments,
					},
					{
						select: monitorPreviewToCounters,
					},
				);

	const { error, data } = useQuery<MonitorStatus | MonitorPreviewResult, Error, MonitorOutputCounters>(query);

	if (error != null) {
		return <GenericErrorMessage className="h-full" error={error} />;
	}

	return (
		<Tabs
			activeKey={selectedTabKey}
			className={styles.tabs}
			destroyInactiveTabPane
			onChange={(tab) => selectedTab.write(tab)}
			overflow="visible"
		>
			{MONITOR_OUTPUT_DRAWER_TABS.map((tab) => ({
				tab,
				healthState: MONITOR_OUTPUT_DRAWER_TAB_TO_HEALTH_STATE[tab],
				count: data == null ? undefined : data[tab],
			}))
				// do not show empty tabs unless the tab is selected
				.filter(({ healthState, count }) => healthState === selectedTabKey || count !== 0)
				.map(({ tab, healthState, count }) => (
					<Tab
						disabled={count == null}
						key={healthState ?? ""}
						tab={
							<MonitorOutputDrawerTabTitle
								count={count}
								healthState={healthState}
								monitor={monitor}
								timestamp={lastSuccessfulRunTimestamp}
							>
								{tab}
							</MonitorOutputDrawerTabTitle>
						}
					>
						<MonitorOutputDrawerScrollToTop>
							<StickyFrame className="bg-grey-100" frames={[spacing(4)]}>
								<MonitorOutputDrawerTable
									count={count}
									filters={filters}
									healthState={healthState}
									monitor={monitor}
									orders={orders}
									timestamp={lastSuccessfulRunTimestamp}
								/>
							</StickyFrame>
						</MonitorOutputDrawerScrollToTop>
					</Tab>
				))}
		</Tabs>
	);
};

const MonitorOutputDrawerTabTitle: React.FC<
	React.PropsWithChildren<{
		monitor: MonitorConfig;
		healthState: undefined | HealthStateValue;
		count: undefined | number;
		timestamp: undefined | Time;
	}>
> = ({ monitor, healthState, count, timestamp, children }) => {
	const query =
		monitor.type === "MONITOR"
			? queryMonitorCheckStates(
					{
						monitorIdOrUrn: monitor.monitorId,
						healthState,
						timestamp,
					},
					{
						select: (checkStates) => checkStates.length,
					},
				)
			: queryTestMonitorCheckStates(
					{
						monitorFunctionIdOrUrn: monitor.monitorFunctionId,
						arguments: monitor.arguments,
						healthState,
					},
					{
						select: (checkStates) => checkStates.length,
					},
				);

	// query the actual check states size from cache, it is likely to arrive faster
	const { data = count } = useQuery({
		...query,
		// do not fetch but extract from cache if fetched anywhere else
		enabled: false,
	});

	return (
		<SkeletonBox el="span" when={data}>
			{children} ({data ?? 0})
		</SkeletonBox>
	);
};

const MonitorOutputDrawerScrollToTop: React.FC<React.PropsWithChildren> = ({ children }) => {
	const scrollBoundaryRef = React.useRef<HTMLDivElement>(null);
	const scrollBoundaryIntersection = useIntersection(scrollBoundaryRef, {
		threshold: 0,
	});

	return (
		<>
			<div ref={scrollBoundaryRef}>{/* show the ScrollToTop as soon as the element is hidden */}</div>

			{children}

			{/**
			 * The absolute position does not work here - the button positioned correctly only when
			 * the scroll is at the top. The sticky position is used to keep the button at the bottom
			 * of the container.
			 *
			 * https://www.horuskol.net/blog/2022-04-13/relative-and-absolute-scrolling-blues/
			 */}
			<div className="sticky bottom-0 z-10 w-full empty:hidden">
				<ScrollToTop
					className="absolute right-0 bottom-0 m-4"
					isVisible={scrollBoundaryIntersection?.isIntersecting === false}
				/>
			</div>
		</>
	);
};
